import axios from 'axios';
import { getSelectedStoreId, getActiveStoreId } from '../../../services/storeService';

const BASEURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const ORDERS_CACHE_MS = 15 * 1000;
const ordersRequestCache = new Map();
const MONTHLY_COUNT_CACHE_MS = 30 * 1000;
let monthlyCountCache = { value: null, at: 0 };
let monthlyCountPromise = null;

const makeOrdersCacheKey = (page, limit, status, search, startDate, endDate, syncroFilter, platformFilter, callFilter, productFilter, echangeFilter, imprimeFilter, emballageFilter) =>
  [page, limit, status, search, startDate, endDate, syncroFilter, platformFilter, callFilter, productFilter, echangeFilter, imprimeFilter, emballageFilter, getSelectedStoreId()].join('|');

const getCachedOrders = (cacheKey) => {
  const cached = ordersRequestCache.get(cacheKey);
  if (!cached) return null;
  if (cached.promise) return cached.promise;
  if (Date.now() - cached.at < ORDERS_CACHE_MS) return Promise.resolve(cached.data);
  ordersRequestCache.delete(cacheKey);
  return null;
};

const setCachedOrders = (cacheKey, data) => {
  ordersRequestCache.set(cacheKey, { data, at: Date.now() });
};

const setPendingOrdersRequest = (cacheKey, promise) => {
  ordersRequestCache.set(cacheKey, { promise, at: Date.now() });
};

export const clearOrdersCache = () => {
  ordersRequestCache.clear();
};

// In order-controller.js
// In order-controller.js
export const fetchOrders = async (page, limit, status, search, startDate, endDate, syncroFilter, platformFilter, callFilter, productFilter, echangeFilter, imprimeFilter, emballageFilter) => {
    try {
    const cacheKey = makeOrdersCacheKey(page, limit, status, search, startDate, endDate, syncroFilter, platformFilter, callFilter, productFilter, echangeFilter, imprimeFilter, emballageFilter);
    const cached = getCachedOrders(cacheKey);
    if (cached) {
      return await cached;
    }

        // Log raw incoming parameters for debugging
        console.log("fetchOrders raw parameters:", {
            page, limit, status, search, startDate, endDate, syncroFilter
        });
        
        // Convert parameters to proper format if needed
        page = parseInt(page) || 1;
        limit = parseInt(limit) || 10;
        
        // Make sure parameters are treated as strings for proper checking
        status = String(status || '');
        search = String(search || '');
        startDate = String(startDate || '');
        endDate = String(endDate || '');
        
        // If endDate is 'true', it means the parameters got mixed up - this is a crucial check!
        if (endDate === 'true' || endDate === 'false' || endDate === 'null') {
            console.warn("Parameter mixup detected! endDate contains syncro value:", endDate);
            // Move the syncro value to syncroFilter and clear endDate
            syncroFilter = endDate;
            endDate = '';
        }

        if(status == "all"){
          status = null;
        }
        
        
        // Build URL with all parameters including platform
        let url = `${BASEURL}/BonCommande/Pagination?PageNumber=${page}&Size=${limit}`;
        
        if (status && status !== 'all') url += `&etat=${encodeURIComponent(status)}`;
        if (search) url += `&searchTerms=${encodeURIComponent(search)}`;
        if (startDate) url += `&dateDebut=${encodeURIComponent(startDate)}`;
        if (endDate) url += `&dateFin=${encodeURIComponent(endDate)}`;
        
        // Only add syncro filter if it's a valid value
        if (syncroFilter === 'true' || syncroFilter === 'false') {
            url += `&syncro=${syncroFilter}`;
        }
        
        // Add platform filter if provided and not 'all'
        if (platformFilter && platformFilter !== '' && platformFilter !== 'all') {
            url += `&platformId=${encodeURIComponent(platformFilter)}`;
        }
        
        // Add call filter if provided
        if (callFilter === 'true' || callFilter === 'false') {
            url += `&callFilter=${callFilter}`;
        }

        // Add echange filter if provided
        if (echangeFilter === 'true' || echangeFilter === 'false') {
            url += `&echange=${echangeFilter}`;
        }

        // Printed / not printed filter
        if (imprimeFilter === 'true' || imprimeFilter === 'false') {
            url += `&imprime=${imprimeFilter}`;
        }

        // Packed / not packed filter
        if (emballageFilter === 'true' || emballageFilter === 'false') {
            url += `&emballage=${emballageFilter}`;
        }
        
        // Add product filter if provided (supports comma-separated IDs for multi-select)
        if (productFilter && productFilter !== '' && productFilter !== 'all') {
            url += `&productIds=${encodeURIComponent(productFilter)}`;
        }
        
        const storeId = getSelectedStoreId();
        if (storeId) url += `&boutiqueId=${encodeURIComponent(storeId)}`;

        // Log the final URL for debugging
        console.log("Final API URL with platform:", url);
        
        const request = axios.get(url, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
        });

        setPendingOrdersRequest(cacheKey, request.then(response => response.data).finally(() => {
          const current = ordersRequestCache.get(cacheKey);
          if (current?.promise) {
            ordersRequestCache.delete(cacheKey);
          }
        }));

        const data = await ordersRequestCache.get(cacheKey).promise;
        setCachedOrders(cacheKey, data);
        return data;
    } catch (error) {
        console.error('Error fetching orders:', error);
        throw error;
    }
};

// Thumbnails for the orders list. Every visible row asks for its own order, so the
// requests made within the same tick are coalesced into a single batched call, and
// results are cached per order id.
const THUMBNAILS_CACHE_MS = 2 * 60 * 1000;
const thumbnailsCache = new Map();
let thumbnailsQueue = [];
let thumbnailsFlushTimer = null;

export const clearOrderThumbnailsCache = () => {
  thumbnailsCache.clear();
};

const flushThumbnailsQueue = async () => {
  const batch = thumbnailsQueue;
  thumbnailsQueue = [];
  thumbnailsFlushTimer = null;
  if (batch.length === 0) return;

  const ids = [...new Set(batch.map((b) => b.orderId))];
  try {
    const url = `${BASEURL}/BonCommande/Thumbnails?ids=${ids.join(',')}`;
    const response = await axios.get(url, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
    });

    const byOrder = new Map();
    (Array.isArray(response.data) ? response.data : []).forEach((row) => {
      byOrder.set(Number(row.bonCommandeId ?? row.BonCommandeId), row.items ?? row.Items ?? []);
    });

    batch.forEach(({ orderId, resolve }) => {
      // An order with no lines simply isn't in the response.
      const items = byOrder.get(Number(orderId)) || [];
      thumbnailsCache.set(Number(orderId), { items, at: Date.now() });
      resolve(items);
    });
  } catch (error) {
    console.error('Error fetching order thumbnails:', error);
    batch.forEach(({ reject }) => reject(error));
  }
};

export const fetchOrderThumbnails = (orderId) => {
  const key = Number(orderId);
  const cached = thumbnailsCache.get(key);
  if (cached && Date.now() - cached.at < THUMBNAILS_CACHE_MS) {
    return Promise.resolve(cached.items);
  }

  return new Promise((resolve, reject) => {
    thumbnailsQueue.push({ orderId: key, resolve, reject });
    // One tick is enough to collect every row of the page being rendered.
    if (!thumbnailsFlushTimer) thumbnailsFlushTimer = setTimeout(flushThumbnailsQueue, 0);
  });
};

// The orders list renders one history button per row, so the same phone can be asked
// for many times at once. Share a single in-flight request per number and reuse the
// answer briefly, instead of firing one call per row.
const PHONE_WARNINGS_CACHE_MS = 60 * 1000;
const phoneWarningsCache = new Map();

export const clearPhoneOrderWarningsCache = (phone) => {
  if (phone) {
    phoneWarningsCache.delete(String(phone));
    phoneHistoryCache.delete(String(phone));
  } else {
    phoneWarningsCache.clear();
    phoneHistoryCache.clear();
  }
};

// Whether a phone has an order history (orders more than 24h apart), for the history icon of
// the orders list. Every row asks at once: the phones asked in the same moment go in a single
// PhonesWithHistory request, and the answers are kept briefly. The history itself is only
// loaded (fetchPhoneOrderWarnings) when the icon is clicked.
const phoneHistoryCache = new Map();
let phoneHistoryBatch = null;

const sendPhoneHistoryBatch = async () => {
  const batch = phoneHistoryBatch;
  phoneHistoryBatch = null;
  const phones = [...batch.keys()];
  try {
    const response = await fetch(`${BASEURL}/BonCommande/PhonesWithHistory`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(phones),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const withHistory = new Set(await response.json());
    phones.forEach((phone) => {
      const value = withHistory.has(phone);
      phoneHistoryCache.set(phone, { value, at: Date.now() });
      batch.get(phone).resolve(value);
    });
  } catch (error) {
    phones.forEach((phone) => {
      phoneHistoryCache.delete(phone);
      batch.get(phone).reject(error);
    });
  }
};

export const hasPhoneHistory = (phone) => {
  const key = String(phone ?? '');
  const cached = phoneHistoryCache.get(key);
  if (cached) {
    if (cached.promise) return cached.promise;
    if (Date.now() - cached.at < PHONE_WARNINGS_CACHE_MS) return Promise.resolve(cached.value);
  }
  if (!phoneHistoryBatch) {
    phoneHistoryBatch = new Map();
    setTimeout(sendPhoneHistoryBatch, 0);
  }
  const entry = {};
  entry.promise = new Promise((resolve, reject) => {
    entry.resolve = resolve;
    entry.reject = reject;
  });
  phoneHistoryBatch.set(key, entry);
  phoneHistoryCache.set(key, { promise: entry.promise });
  return entry.promise;
};

export const fetchPhoneOrderWarnings = async (phone) => {
  const key = String(phone ?? '');
  const cached = phoneWarningsCache.get(key);
  if (cached) {
    if (cached.promise) return cached.promise;
    if (Date.now() - cached.at < PHONE_WARNINGS_CACHE_MS) return cached.data;
    phoneWarningsCache.delete(key);
  }

  const request = (async () => {
    const url = `${BASEURL}/BonCommande/WarnningNumberTel?Tel=${encodeURIComponent(phone)}`;
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  })();

  phoneWarningsCache.set(key, { promise: request, at: Date.now() });

  try {
    const data = await request;
    phoneWarningsCache.set(key, { data, at: Date.now() });
    return data;
  } catch (error) {
    // Drop the failed entry so the next caller can retry.
    phoneWarningsCache.delete(key);
    console.error('Error fetching phone order warnings:', error);
    throw error;
  }
};

export const createOrder = async (orderData) => {
  try {
      // Attribute the order to the active store
      const storeId = await getActiveStoreId();
      if (storeId && orderData && typeof orderData === 'object') {
        const target = orderData.entet || orderData.Entet;
        if (target && target.boutiqueId == null) target.boutiqueId = Number(storeId);
      }
      
      const response = await axios.post(`${BASEURL}/BonCommande/save`, orderData, {
          headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
      });
      
      // A new order changes a client's history, so drop the cached warnings.
      clearPhoneOrderWarningsCache();

      return response.data;
  } catch (error) {
      console.error('Error in createOrder API call:', error);
      
      if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.error('Response data:', error.response.data);
          console.error('Response status:', error.response.status);
          console.error('Response headers:', error.response.headers);
      } else if (error.request) {
          // The request was made but no response was received
          console.error('No response received:', error.request);
      } else {
          // Something happened in setting up the request that triggered an Error
          console.error('Error message:', error.message);
      }
      
      throw error;
  }
};

export const updateOrder = async (orderId, orderData) => {
  try {
      
      const response = await axios.put(`${BASEURL}/BonCommande/update/${orderId}`, orderData, {
          headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
      });
      
      return response.data;
  } catch (error) {
      console.error('Error in updateOrder API call:', error);
      
      if (error.response) {
          console.error('Response data:', error.response.data);
          console.error('Response status:', error.response.status);
          console.error('Response headers:', error.response.headers);
      } else if (error.request) {
          console.error('No response received:', error.request);
      } else {
          console.error('Error message:', error.message);
      }
      
      throw error;
  }
};

export const fetchOrderById = async (orderId) => {
  try {
    const response = await fetch(`${BASEURL}/BonCommande/ById/${orderId}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Failed to fetch order with ID ${orderId}:`, error);
    throw error;
  }
};

// Fetch the stock / picking list (products, variant name and quantity)
// for a set of selected orders in a single request.
export const fetchOrdersStock = async (orderIds = []) => {
  try {
    const response = await axios.post(`${BASEURL}/BonCommande/PrintStock`, orderIds, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch orders stock:', error);
    throw error;
  }
};


// Mark the selected DELIVERED orders as PAID.
// Only orders currently in the "delivered" state are updated (handled server-side).
export const markDeliveredAsPaid = async (orderIds = []) => {
  try {
    const response = await axios.patch(`${BASEURL}/BonCommande/MarkDeliveredAsPaid`, orderIds, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to mark delivered orders as paid:', error);
    throw error;
  }
};

// Mark orders as printed once their labels have been sent to the printer.
export const markOrdersPrinted = async (orderIds = []) => {
  if (!orderIds.length) return { updated: 0 };
  const response = await axios.patch(`${BASEURL}/BonCommande/MarkPrinted`, orderIds, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('authToken')}`
    }
  });
  clearOrdersCache();
  return response.data;
};

// Emballage page: the scanned delivery code (code livraison) marks its order as packed.
// Returns { success, dejaEmballee?, message, reference?, client?, etat?, orderId? }.
// Edit order: sets or clears "Emballée" on one order.
export const setOrderEmballage = async (orderId, emballage) => {
  const response = await axios.patch(
    `${BASEURL}/BonCommande/Emballage/${orderId}`,
    JSON.stringify(Boolean(emballage)),
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    }
  );
  return response.data;
};

export const markEmballageByCode = async (codeLivraison) => {
  const response = await axios.patch(
    `${BASEURL}/BonCommande/EmballageByCode`,
    JSON.stringify(codeLivraison),
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    }
  );
  return response.data;
};

// Scan a delivery code (code livraison) and mark the matching order as "return received",
// but only if it is currently in "return" status (validated server-side).
// Returns { success, message, reference?, etat?, orderId? }.
export const markReturnReceivedByCode = async (codeLivraison) => {
  try {
    const response = await axios.patch(
      `${BASEURL}/BonCommande/ReturnReceivedByCode`,
      JSON.stringify(codeLivraison),
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to mark order as return received:', error);
    throw error;
  }
};


export const updateOrderStatus = async (orderId, status, orderIds = []) => {
  try {
    // Determine which data to send based on parameters
    let requestData;
    
    if (orderId) {
      // If orderId is provided, use single order update
      requestData = { id: [orderId], etat: status };
    } else if (orderIds && orderIds.length > 0) {
      // If orderIds array is provided and not empty, use batch update
      requestData = { 
        id: orderIds,
        etat: status 
      };
    } else {
      // If neither is provided, throw an error
      throw new Error('Either orderId or orderIds must be provided');
    }
    
    const response = await axios.patch(`${BASEURL}/BonCommande/updatestatus`, requestData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    
    return response.data;
  } catch (error) {
    // Enhanced error logging
    if (orderId) {
      console.error(`Error in updateOrderStatus API call for order ${orderId} and status ${status}:`, error);
    } else {
      console.error(`Error in updateOrderStatus API call for ${orderIds?.length || 0} orders and status ${status}:`, error);
    }
    
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    
    throw error;
  }
};
export const fetchOrdersHeader = async () => {
  try {
    let url = `${BASEURL}/boncommande/getheader`;
        
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch Orders:', error);
    throw error;
  }
};

export const fetchStatusHistory = async (orderId) => {
  try {
    const response = await fetch(`${BASEURL}/BonCommande/StatusHistory/${orderId}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Failed to fetch status history for order ${orderId}:`, error);
    return [];
  }
};

// Fetch monthly order count (current month)
export const fetchMonthlyOrderCount = async () => {
  try {
    if (monthlyCountCache.value !== null && Date.now() - monthlyCountCache.at < MONTHLY_COUNT_CACHE_MS) {
      return monthlyCountCache.value;
    }

    if (monthlyCountPromise) {
      return await monthlyCountPromise;
    }

    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const startDate = `${year}-${month}-01`;
    
    // Calculate last day of month
    const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
    const endDate = `${year}-${month}-${lastDay}`;
    
    const url = `${BASEURL}/boncommande?page=1&size=1&startDate=${startDate}&endDate=${endDate}`;
        
    monthlyCountPromise = fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      const total = data.total || 0;
      monthlyCountCache = { value: total, at: Date.now() };
      return total;
    }).finally(() => {
      monthlyCountPromise = null;
    });

    return await monthlyCountPromise;
  } catch (error) {
    console.error('Failed to fetch monthly order count:', error);
    return 0;
  }
};

export const fetchOrderNewReference = async () =>{
  try {
    let url = `${BASEURL}/numerodocument/boncommande`;
        
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch Orders:', error);
    throw error;
  }
}

export const syncronizeOrders = async (orders) => {
  try {
    let url = `${BASEURL}/boncommande`;
    console.log('those are the orders to sync', orders);
    const response = await axios.patch(url, orders, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to synchronize orders:', error);
    throw error;
  }
}

export const syncronizeOneOrder = async (orderId) => {
  try {
    const response = await axios.patch(`${BASEURL}/boncommande/SyncOne/${orderId}`, null, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to synchronize order ${orderId}:`, error);
    throw error;
  }
}

export const deleteOrder = async (orderId) => {
  try {
    const response = await axios.delete(`${BASEURL}/BonCommande/${orderId}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    // No need to check response.ok with axios!
    return response.data;
  } catch (error) {
    console.error(`Failed to delete order with ID ${orderId}:`, error);
    throw error;
  }
};

export const updateOrderProduct = async (orderData) => {
  try {
    // Replace with your actual API endpoint for updating orders
    const response = await axios.patch(`${process.env.REACT_APP_API_URL}/BonCommande/PatchAbondonnedBonCommande`, orderData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to update order:', error);
    throw error;
  }
};

// Add this new function to the end of the file
export const trackOrders = async (orderIds) => {
  try {
    console.log('Orders to track:', orderIds);
    
    const response = await axios.patch(`${BASEURL}/BonCommande/Tracking`, orderIds, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to track orders:', error);
    
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    
    throw error;
  }
};

export const fetchOrdersWithFilters = async (page = 1, limit = 10, status = '', search = '', startDate = '', endDate = '', syncroFilter = '', platformFilter = '') => {
    try {
    const cacheKey = makeOrdersCacheKey(page, limit, status, search, startDate, endDate, syncroFilter, platformFilter);
    const cached = getCachedOrders(cacheKey);
    if (cached) {
      return await cached;
    }

        console.log("Fetching orders with filters:", {
            page, limit, status, search, startDate, endDate, syncroFilter, platformFilter
        });

        // Validate parameters and handle edge cases
        if (typeof endDate === 'boolean' || (endDate && endDate.length < 5)) {
            console.warn("Parameter mixup detected! endDate contains syncro value:", endDate);
            // Move the syncro value to syncroFilter and clear endDate
            syncroFilter = endDate;
            endDate = '';
        }

        if(status == "all"){
          status = null;
        }
        
        
        // Build URL with validated parameters
        let url = `${BASEURL}/BonCommande/Pagination?PageNumber=${page}&Size=${limit}`;
        
        if (status) url += `&etat=${encodeURIComponent(status)}`;
        if (search) url += `&searchTerms=${encodeURIComponent(search)}`;
        if (startDate) url += `&dateDebut=${encodeURIComponent(startDate)}`;
        if (endDate) url += `&dateFin=${encodeURIComponent(endDate)}`;
        
        // Only add syncro filter if it's a valid value
        if (syncroFilter === 'true' || syncroFilter === 'false') {
            url += `&syncro=${syncroFilter}`;
        }
        
        // Add platform filter if provided
        if (platformFilter && platformFilter !== '' && platformFilter !== 'all') {
            url += `&platformId=${encodeURIComponent(platformFilter)}`;
        }

        const storeId = getSelectedStoreId();
        if (storeId) url += `&boutiqueId=${encodeURIComponent(storeId)}`;

        console.log("Final API URL:", url);
        
        const responsePromise = fetch(url, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
        });

        setPendingOrdersRequest(cacheKey, responsePromise.then(async (response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return await response.json();
        }).finally(() => {
          const current = ordersRequestCache.get(cacheKey);
          if (current?.promise) {
            ordersRequestCache.delete(cacheKey);
          }
        }));

        const data = await ordersRequestCache.get(cacheKey).promise;
        setCachedOrders(cacheKey, data);
        console.log("API Response:", data);
        return data;
    } catch (error) {
        console.error('Error fetching orders:', error);
        throw error;
    }
};

// Add this new function to the existing order-controller.js file

export const importOrders = async (barcodes) => {
    try {
        console.log('Importing orders with barcodes:', barcodes);

        const response = await fetch(`${BASEURL}/boncommande/import`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            },
            body: JSON.stringify(barcodes)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('Import successful:', result);
        return result;
    } catch (error) {
        console.error('Error importing orders:', error);
        throw error;
    }
};

// New function to update the call state for an order
export const updateOrderAvisClient = async (orderId, avisClient) => {
  try {
    const url = `${BASEURL}/BonCommande/AvisClient/${orderId}`;
    const response = await axios.patch(url, JSON.stringify(avisClient), {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to update avisClient for order ${orderId}:`, error);
    throw error;
  }
};

export const updateOrderCommentaire = async (orderId, commentaire) => {
  try {
    const url = `${BASEURL}/BonCommande/Commentaire/${orderId}`;
    const response = await axios.patch(url, JSON.stringify(commentaire), {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to update commentaire for order ${orderId}:`, error);
    throw error;
  }
};

export const updateOrderAdresse = async (orderId, adresse) => {
  try {
    const url = `${BASEURL}/BonCommande/Adresse/${orderId}`;
    const response = await axios.patch(url, JSON.stringify(adresse), {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to update adresse for order ${orderId}:`, error);
    throw error;
  }
};

// `ville` is the city code (e.g. "TUN"), matching what the create/edit order form sends.
export const updateOrderVille = async (orderId, ville) => {
  try {
    const url = `${BASEURL}/BonCommande/Ville/${orderId}`;
    const response = await axios.patch(url, JSON.stringify(ville), {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to update ville for order ${orderId}:`, error);
    throw error;
  }
};

// Increments the order's call-attempt counter by 1 and returns the new count.
export const incrementOrderCall = async (orderId) => {
  try {
    const url = `${BASEURL}/BonCommande/Call/${orderId}`;
    const response = await axios.patch(url, null, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });

    return response.data;
  } catch (error) {
    console.error(`Failed to increment call count for order ${orderId}:`, error);
    throw error;
  }
};

// Decrements the order's call-attempt counter by 1 (floored at 0) and returns the new count.
export const decrementOrderCall = async (orderId) => {
  try {
    const url = `${BASEURL}/BonCommande/Call/${orderId}/decrement`;
    const response = await axios.patch(url, null, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });

    return response.data;
  } catch (error) {
    console.error(`Failed to decrement call count for order ${orderId}:`, error);
    throw error;
  }
};

export const fetchOrderLines = async (orderId) => {
  try {
    const response = await axios.get(`${BASEURL}/BonCommande/Lines/${orderId}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch lines for order ${orderId}:`, error);
    throw error;
  }
};

export const quickUpdateVariant = async (ligneBonCommandeId, newVariantId) => {
  try {
    const response = await axios.patch(`${BASEURL}/BonCommande/QuickUpdateVariant`, {
      ligneBonCommandeId,
      newVariantId
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to update variant:`, error);
    throw error;
  }
};